<template>
  <div class="card">
    <div class="p-3 pb-0 card-header">
      <div class="d-flex justify-content-between">
        <h6 class="mb-0">Tiempos por Canasta</h6>
      </div>
      <p class="text-xs text-secondary mb-2">Tiempo desde que se recibe la canasta hasta que se entrega.</p>
      <div class="row g-2">
        <div class="col-md-3">
          <select v-model="filters.production_id" class="form-select form-select-sm">
            <option value="">Todas las producciones</option>
            <option v-for="p in productions" :key="p.id" :value="p.id">#{{ p.id }} - {{ p.name }}</option>
          </select>
        </div>
        <div class="col-md-2">
          <select v-model="filters.basket_id" class="form-select form-select-sm" :disabled="!filters.production_id"
            :title="filters.production_id ? '' : 'Seleccione una producción'">
            <option value="">Todas las canastas</option>
            <option v-for="b in baskets" :key="b.id" :value="b.id">Canasta #{{ b.number }} ({{ b.quantity }} u.)</option>
          </select>
        </div>
        <div class="col-md-2">
          <select v-model="filters.group_id" class="form-select form-select-sm">
            <option value="">Todos los grupos</option>
            <option v-for="group in groups" :key="group.id" :value="group.id">{{ group.name }}</option>
          </select>
        </div>
        <div class="col-md-2">
          <select v-model="filters.oficio_id" class="form-select form-select-sm">
            <option value="">Todos los oficios</option>
            <option v-for="oficio in oficios" :key="oficio.id" :value="oficio.id">{{ oficio.name }}</option>
          </select>
        </div>
        <div class="col-md-3 d-flex gap-2">
          <input type="date" v-model="filters.start_date" class="form-control form-control-sm" title="Asignada desde">
          <input type="date" v-model="filters.end_date" class="form-control form-control-sm" title="Asignada hasta">
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" title="Limpiar filtros" @click="resetFilters">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="p-3 card-body">
      <div class="row g-2 mb-3 text-center">
        <div class="col-6 col-md-2">
          <div class="stat-box">
            <span class="text-xs text-secondary d-block">Entregadas</span>
            <span class="font-weight-bold">{{ summary.completed || 0 }}</span>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <div class="stat-box">
            <span class="text-xs text-secondary d-block">En proceso</span>
            <span class="font-weight-bold">{{ summary.in_progress || 0 }}</span>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <div class="stat-box">
            <span class="text-xs text-secondary d-block">Promedio por canasta</span>
            <span class="font-weight-bold">{{ formatMinutes(summary.avg_minutes) }}</span>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <div class="stat-box">
            <span class="text-xs text-secondary d-block">Promedio por unidad</span>
            <span class="font-weight-bold">{{ formatMinutes(summary.avg_minutes_per_unit) }}</span>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <div class="stat-box">
            <span class="text-xs text-secondary d-block">Más rápida</span>
            <span class="font-weight-bold">{{ formatMinutes(summary.min_minutes) }}</span>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <div class="stat-box">
            <span class="text-xs text-secondary d-block">Más lenta</span>
            <span class="font-weight-bold">{{ formatMinutes(summary.max_minutes) }}</span>
          </div>
        </div>
      </div>

      <div class="chart" :style="{ height: chartHeight + 'px' }" v-show="chartTasks.length">
        <canvas ref="canvas"></canvas>
      </div>
      <p class="text-center text-sm mb-0" v-if="!loading && !chartTasks.length">
        No hay canastas con tiempos registrados para estos filtros
      </p>

      <div class="table-responsive mt-3" style="max-height: 320px; overflow-y: auto;" v-if="tasks.length">
        <table class="table align-items-center mb-0">
          <thead class="sticky-top bg-white">
            <tr class="text-xxs text-uppercase text-secondary">
              <th>Canasta</th>
              <th>Oficio</th>
              <th>Grupo / Operario</th>
              <th class="text-center">Cant.</th>
              <th class="text-center">Recibida</th>
              <th class="text-center">Entregada</th>
              <th class="text-center">Duración</th>
            </tr>
          </thead>
          <tbody class="text-xs">
            <tr v-for="t in tasks" :key="t.task_id">
              <td>#{{ t.basket_number }}</td>
              <td>{{ t.oficio || '-' }}</td>
              <td>
                <span v-if="t.group" class="d-block font-weight-bold">{{ t.group }}</span>
                <span>{{ t.users.join(', ') || '-' }}</span>
              </td>
              <td class="text-center">{{ t.quantity }}</td>
              <td class="text-center">{{ formatDateTime(t.start) }}</td>
              <td class="text-center">{{ t.end ? formatDateTime(t.end) : 'En proceso' }}</td>
              <td class="text-center">
                <span v-if="t.minutes === null" class="text-secondary" title="Registro sin hora de recibido/entregado">Sin hora</span>
                <span v-else class="badge" :class="t.in_progress ? 'bg-gradient-warning' : 'bg-gradient-success'">
                  {{ formatMinutes(t.minutes) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

const emptyFilters = () => ({ production_id: '', basket_id: '', group_id: '', oficio_id: '', start_date: '', end_date: '' });

export default {
  name: "basket-times-chart",
  props: {
    productions: { type: Array, default: () => [] },
    groups: { type: Array, default: () => [] },
    oficios: { type: Array, default: () => [] },
  },
  data() {
    return {
      filters: emptyFilters(),
      tasks: [],
      baskets: [],
      summary: {},
      loading: false,
      timer: null,
    };
  },
  computed: {
    chartTasks() {
      return this.tasks.filter(t => t.minutes !== null);
    },
    chartHeight() {
      return Math.max(220, this.chartTasks.length * 32 + 60);
    },
  },
  watch: {
    'filters.production_id'() {
      this.filters.basket_id = '';
    },
    filters: {
      deep: true,
      handler() {
        clearTimeout(this.timer);
        this.timer = setTimeout(this.fetchData, 400);
      },
    },
  },
  methods: {
    resetFilters() {
      this.filters = emptyFilters();
    },
    async fetchData() {
      this.loading = true;
      try {
        const params = Object.fromEntries(Object.entries(this.filters).filter(([, v]) => v !== '' && v !== null));
        const response = await this.$store.dispatch('analitycs/getBasketTimes', params);
        const data = response?.data || {};
        this.tasks = Array.isArray(data.tasks) ? data.tasks : [];
        this.baskets = Array.isArray(data.baskets) ? data.baskets : [];
        this.summary = data.summary || {};
      } catch (e) {
        this.tasks = [];
        this.baskets = [];
        this.summary = {};
      }
      this.loading = false;
      this.$nextTick(this.renderChart);
    },
    renderChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      if (!this.chartTasks.length || !this.$refs.canvas) return;

      const tasks = this.chartTasks;
      this.chart = new Chart(this.$refs.canvas.getContext("2d"), {
        type: "bar",
        data: {
          labels: tasks.map(t => `#${t.basket_number} · ${t.oficio || ''} · ${t.group || t.users.join(', ')}`),
          datasets: [{
            label: "Duración",
            data: tasks.map(t => t.minutes),
            backgroundColor: tasks.map(t => t.in_progress ? "#fb6340" : "#2dce89"),
            borderRadius: 4,
            maxBarThickness: 22,
          }],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const t = tasks[ctx.dataIndex];
                  return `${this.formatMinutes(t.minutes)}${t.in_progress ? ' (en proceso)' : ''} · ${t.quantity} u.`;
                },
                afterLabel: (ctx) => tasks[ctx.dataIndex].users.join(', '),
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              title: { display: true, text: "Tiempo" },
              ticks: { callback: (value) => this.formatMinutes(value) },
            },
            y: { ticks: { font: { size: 11 } } },
          },
        },
      });
    },
    formatMinutes(minutes) {
      if (minutes === null || minutes === undefined) return '-';
      if (minutes < 1) return `${Math.round(minutes * 60)} s`;
      const total = Math.round(minutes);
      const days = Math.floor(total / 1440);
      const hours = Math.floor((total % 1440) / 60);
      const mins = total % 60;
      if (days) return `${days}d ${hours}h`;
      if (hours) return `${hours}h ${mins}m`;
      return `${mins}m`;
    },
    formatDateTime(date) {
      return new Date(date).toLocaleString('es-ES', {
        day: '2-digit', month: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    },
  },
  mounted() {
    this.fetchData();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
    if (this.chart) this.chart.destroy();
  },
};
</script>

<style scoped>
.stat-box {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.5rem;
}
</style>
